// src/components/ApplicationStatusCheck.jsx

import React, { useState } from 'react';
import { useWallet } from '../context/WalletContext';
import * as api from '../services/api';
import '../styles/application-status.css';

/**
 * Component to check application status by reference code
 */
const ApplicationStatusCheck = () => {
  const { wallet, openWalletModal } = useWallet();
  const [referenceCode, setReferenceCode] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');
  const [applicationStatus, setApplicationStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!referenceCode.trim()) {
      setError('Please enter a valid reference code');
      return;
    }
    
    if (!wallet.connected || !wallet.address) {
      // Store the reference code for after wallet connection
      localStorage.setItem('pending_reference_code', referenceCode);
      openWalletModal();
      return;
    }
    
    setIsChecking(true);
    setError('');
    setApplicationStatus(null);
    
    try {
      const status = await api.checkApplicationStatus(
        referenceCode,
        wallet.address
      );
      
      setApplicationStatus(status);
    } catch (err) {
      console.error('Failed to check application status:', err);
      
      // Extract detailed error message if available
      let errorMessage = 'Failed to retrieve application status. Please check your reference code and try again.';
      
      try {
        if (err.response && err.response.data) {
          const errorData = err.response.data;
          errorMessage = errorData.message || errorMessage;
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      setError(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };
  
  // Check for pending reference code after wallet connection
  React.useEffect(() => {
    if (wallet.connected) {
      const pendingRefCode = localStorage.getItem('pending_reference_code');
      if (pendingRefCode) {
        setReferenceCode(pendingRefCode);
        localStorage.removeItem('pending_reference_code');
        
        // Submit the form after a short delay
        setTimeout(() => {
          document.getElementById('status-check-form').dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
          );
        }, 500);
      }
    }
  }, [wallet.connected]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Get status class
  const getStatusClass = (status) => {
    const statusMap = {
      'submitted': 'submitted',
      'pending_payment': 'pending_payment',
      'processing': 'processing',
      'approved': 'approved',
      'rejected': 'rejected',
      'active': 'approved',
      'suspended': 'rejected',
      'terminated': 'rejected'
    };
    
    return statusMap[status] || '';
  };

  return (
    <div className="application-status-check">
      <h3>Check Application Status</h3>
      
      <form id="status-check-form" onSubmit={handleSubmit} className="status-check-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="referenceCode">Application Reference Code</label>
            <input
              type="text"
              id="referenceCode"
              value={referenceCode}
              onChange={(e) => setReferenceCode(e.target.value)}
              placeholder="ALD-12345"
              disabled={isChecking}
              className={error ? 'error' : ''}
            />
          </div>
          
          <button 
            type="submit" 
            className="btn check-status-btn" 
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <span className="spinner small"></span>
                <span>Checking...</span>
              </>
            ) : !wallet.connected ? (
              'Connect & Check'
            ) : (
              'Check Status'
            )}
          </button>
        </div>
        
        {error && (
          <div className="status-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{error}</span>
          </div>
        )}
      </form>
      
      {applicationStatus && (
        <div className="status-result">
          <div className="status-header">
            <div className="status-ref">Ref: {applicationStatus.reference_code}</div>
            <div className={`status-badge ${getStatusClass(applicationStatus.status)}`}>
              {applicationStatus.status.replace('_', ' ')}
            </div>
          </div>
          
          <div className="status-details">
            <div className="status-detail-row">
              <div className="detail-label">Tier:</div>
              <div className="detail-value">{applicationStatus.tier}</div>
            </div>
            <div className="status-detail-row">
              <div className="detail-label">Applied On:</div>
              <div className="detail-value">{formatDate(applicationStatus.created_at)}</div>
            </div>
            <div className="status-detail-row">
              <div className="detail-label">Last Updated:</div>
              <div className="detail-value">{formatDate(applicationStatus.updated_at)}</div>
            </div>
            <div className="status-detail-row">
              <div className="detail-label">Payment:</div>
              <div className="detail-value">
                <span className={applicationStatus.payment_confirmed ? 'confirmed' : 'pending'}>
                  {applicationStatus.payment_confirmed ? 'Confirmed' : 'Pending'}
                </span>
              </div>
            </div>
          </div>
          
          {applicationStatus.message && (
            <div className="status-message">
              <div className="message-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#3b82f6" strokeWidth="2"/>
                  <path d="M12 8V12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M12 16H12.01" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <p>{applicationStatus.message}</p>
            </div>
          )}
          
          {/* Show relevant action buttons based on status */}
          {applicationStatus.status === 'pending_payment' && wallet.connected && (
            <div className="status-actions">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  // Redirect to node application list where payment can be submitted
                  document.getElementById('node-application').scrollIntoView({ behavior: 'smooth' });
                  // Highlight the application
                  const appElement = document.querySelector(`[data-reference="${applicationStatus.reference_code}"]`);
                  if (appElement) {
                    appElement.classList.add('highlight');
                    setTimeout(() => appElement.classList.remove('highlight'), 3000);
                  }
                }}
              >
                Go to Payment
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationStatusCheck;
